import type { UseDroppableArguments } from "@dnd-kit/core";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { cloneDeep, findIndex, orderBy } from "lodash-es";
import { plural } from "pluralize";
import {
  type CSSProperties,
  type FC,
  type PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type AriaCheckboxProps, VisuallyHidden, mergeProps, useCheckbox } from "react-aria";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useToggleState } from "react-stately";

import { ActionButtonIntent, HStack, HeaderBar, SymbolIcon, Tooltip, TooltipIntent, useTooltip } from "widgets";
import { useDOMRef } from "widgets/hooks";

import styleContextualMenu from "../core/styles/ContextualMenu.module.scss";
import style from "./styles/TrackSelectionDialog.module.scss";

import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import { SelectAllNoneWidget } from "../core/forms/SelectWidget.js";
import { Dialog } from "../core/overlays/Dialogs.js";
import { ModalControlledOverlay } from "../core/overlays/index.js";
import {
  conditionallyPluralizeTrackLabel,
  generateAttributeStringCompare,
  pluralizeWord,
  splitIntoMultipleSearchTerms,
} from "@/app-utils/string.ts";
import {
  type AaFeatureStats,
  type GenomeBrowserStatistics,
  type InputTrack,
  type InputUserParamsInput,
  type MarkerStats,
  type NuFeatureStats,
  type NuProfileStats,
  type Organism,
  type Track,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import { updateUserParams } from "@/redux/modules/auth.ts";
import { type ISortableTrack, PIN_TRACK } from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { GREY_COLOR, SPECIAL_COLOR } from "@/settings/colors-settings.ts";
import { FEATURE_DISPLAY_NAME, TRACK_TYPE } from "@/settings/settings.ts";

// TODO Add the Flags track here too?

export interface ITrackSelectionDialogProps {
  show: boolean;
  tracks: ISortableTrack[];
  genome: Organism;
  hideBlast?: boolean;
  onApply: (tracks: ISortableTrack[]) => void;
  onCancel: () => void;
}

const TOP_TRACKS_LIST_ID = "top-tracks-list";
const MIDDLE_TRACKS_LIST_ID = "middle-tracks-list";
const BOTTOM_TRACKS_LIST_ID = "bottom-tracks-list";

const TrackSelectionDialog: FC<ITrackSelectionDialogProps> = (props) => {
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(pointerSensor, keyboardSensor);
  const dispatch = useDispatch<ReduxDispatch>();

  const compareTracks = generateAttributeStringCompare(["type", "subTypeId", "pinned"]);

  const statistics = useSelector<IAppState, GenomeBrowserStatistics>(
    (state) => state.genomeBrowser.statistics,
    shallowEqual,
  );
  const [filter, setFilter] = useState<string>("");
  const [tracks, setTracks] = useState<ISortableTrack[]>([]);
  const [saveTracksTooltip, setSaveTracksTooltip] = useState<string>(null);

  const [draggedTrack, setDraggedTrack] = useState<ISortableTrack>();

  const userDefinedTracks = useSelector<IAppState, Track[]>(
    (state) => state.auth.session?.params?.defaultTracks ?? [],
    shallowEqual,
  );

  const middleTracksScrollableList = useMemo(() => tracks.filter((track) => !track.pinned), [tracks]);
  const topTracksPinnedList = useMemo(() => tracks.filter((track) => track.pinned === PIN_TRACK.TOP), [tracks]);
  const bottomTracksPinnedList = useMemo(() => tracks.filter((track) => track.pinned === PIN_TRACK.BOTTOM), [tracks]);

  const currentVisibleTracksLength = tracks.filter((track) => !track.hidden).length;
  const visibleTracksMaxLength = tracks.filter((track) => !noTrackDataAvailable(track, statistics)).length;

  const currentlySelectedTracks = tracks.filter((track) => !track.hidden);

  const initState = useCallback(
    (tracks: ISortableTrack[]) => {
      // Order tracks by visible first and then all hidden by name
      // Sequence track is always on top as it is currently sticky on top (and not draggable)
      let allTracks = orderBy(
        tracks,
        [(t) => t.hidden, (t) => (t.type === TRACK_TYPE.SEQUENCE ? 0 : 1)],
        ["asc", "asc"],
      );

      if (props.hideBlast) {
        allTracks = allTracks.filter((t) => t.type !== TRACK_TYPE.BLAST_ALIGNMENT);
      }

      setTracks(allTracks);
    },
    [props.hideBlast],
  );

  const onCheckboxClick = useCallback(
    (isSelected: boolean, track: ISortableTrack) => {
      const newTracks: ISortableTrack[] = cloneDeep(tracks);

      // Update the hidden state of our copy of the column list,
      // then create a new set of filtered columns based on it (including the last "hidden" change),
      // and save both to the state
      const pos = newTracks.findIndex((t) => {
        return t.name === track.name && t.type === track.type;
      });
      newTracks[pos].hidden = !isSelected;

      // set filteredColumns and change column selection in local state
      setTracks(newTracks);
    },
    [tracks],
  );

  const onFilter = (filterTerm: string, track: ISortableTrack): boolean => {
    const filterTerms: string[] = splitIntoMultipleSearchTerms(filterTerm);
    return filterTerms.every(
      (term: string) =>
        plural(track.name!).toLowerCase().includes(term.toLowerCase()) ||
        FEATURE_DISPLAY_NAME[track.type]?.toLowerCase().includes(term.toLowerCase()),
    );
  };

  const onSaveTrackList = useCallback(() => {
    const selectedTracks: InputTrack[] = tracks
      .filter((track) => !track.hidden)
      .map((track) => {
        return {
          name: null,
          type: track.type,
          subTypeId: track.subTypeId,
          pinned: track.pinned,
          trackCustomization: null,
        } as InputTrack;
      });

    const payload: InputUserParamsInput = { defaultTracks: [...selectedTracks] };
    dispatch(updateUserParams(payload)).then(() =>
      notify.info({
        title: selMessage({ id: "genomeViewer.saveTrackLayout.successTitle" }),
        children: SelMessage({ id: "genomeViewer.saveTrackLayout.successMessage" }),
      }),
    );
  }, [tracks]);

  /**
   * Checks equality of two ITrack arrays in terms of order, type,
   * subtype and pinned state.
   *
   * If both arrays are unequal by length the user has selected other
   * tracks than already stored in the DB.
   * If they are of equal length the tracks are compared by their type,
   * subtype, pinned state and order.
   * @param arr1
   * @param arr2
   */
  const equalTrackArrays = (arr1: Track[], arr2: Track[]): boolean => {
    if (arr1.length !== arr2.length) {
      return false;
    }
    for (let i = 0; i < arr1.length; i++) {
      if (compareTracks(arr1[i], arr2[i]) !== 0) {
        return false;
      }
    }
    return true;
  };

  const savingOfTracksAllowed = () => {
    if (!currentVisibleTracksLength) {
      // if no track is selected
      setSaveTracksTooltip("Select at least one track to save.");
    } else {
      const tracksIdentical = equalTrackArrays(currentlySelectedTracks, userDefinedTracks);

      if (tracksIdentical) {
        setSaveTracksTooltip("Selected tracks are already saved.");
      } else {
        setSaveTracksTooltip(null);
      }
    }
  };

  useEffect(() => {
    if (props.tracks) {
      initState(props.tracks);
    }
  }, [initState, props.tracks]);

  useEffect(() => {
    savingOfTracksAllowed();
  }, [currentlySelectedTracks]);

  return (
    <ModalControlledOverlay
      isOpen={true}
      title={"Modal"}
      close={() => {
        initState(props.tracks);
        props.onCancel();
      }}
    >
      {({ close }) => (
        <Dialog
          mainActionButtonProps={{ children: "Apply", onPress: () => props.onApply(tracks) }}
          secondaryActionButtonProps={{
            children: "Save Track Layout",
            onPress: () => onSaveTrackList(),
            isDisabled: !!saveTracksTooltip,
            tooltip: saveTracksTooltip ?? "Click to save a set of default tracks.",
            intent: saveTracksTooltip ? ActionButtonIntent.Secondary : ActionButtonIntent.Outlined,
          }}
          closeFn={close}
          noCardPadding
        >
          <div className={style.card}>
            <HeaderBar
              title="Manage Tracks"
              actions={[
                <AsyncSearchWithExprParser
                  key={"track-selection-dialog-widget-1"}
                  onSearchExprChange={async (value) => setFilter(value)}
                />,
                <SelectAllNoneWidget
                  key={"track-selection-dialog-widget-2"}
                  current={currentVisibleTracksLength}
                  max={visibleTracksMaxLength}
                  label={"View"}
                  onSelectAll={(all) => {
                    const clonedTracks = tracks.map((track) => ({
                      ...track,
                      hidden: noTrackDataAvailable(track, statistics) ? true : !all,
                    }));

                    setTracks(clonedTracks);
                  }}
                />,
              ]}
            />
            <div className={style.card_content}>
              <DndContext
                sensors={sensors}
                onDragStart={({ active }) => {
                  setDraggedTrack(active.data.current?.track);
                }}
                onDragEnd={({ active, over }) => {
                  const oldIndex = findIndex<ISortableTrack>(tracks, (t) => getTrackType(t) === active.id);
                  const newIndex = findIndex<ISortableTrack>(tracks, (t) => getTrackType(t) === over?.id);
                  setTracks(arrayMove(tracks, oldIndex, newIndex));
                  setDraggedTrack(undefined);
                }}
                onDragOver={({ active, over }) => {
                  // Change the pinned flag of the track.
                  // We test if we are over the droppable list OR directly over an element from the other list.
                  if (over?.id === TOP_TRACKS_LIST_ID || over?.data.current?.track?.pinned === PIN_TRACK.TOP) {
                    setTracks(
                      tracks.map((track) => ({
                        ...track,
                        pinned: active.id === getTrackType(track) ? PIN_TRACK.TOP : track.pinned,
                      })),
                    );
                  } else if (
                    over?.id === BOTTOM_TRACKS_LIST_ID ||
                    over?.data.current?.track?.pinned == PIN_TRACK.BOTTOM
                  ) {
                    setTracks(
                      tracks.map((track) => ({
                        ...track,
                        pinned: active.id === getTrackType(track) ? PIN_TRACK.BOTTOM : track.pinned,
                      })),
                    );
                  } else if (over?.id === MIDDLE_TRACKS_LIST_ID || over?.data.current?.track?.pinned == undefined) {
                    //! Because we test against "undefined", this needs to be the last test.
                    setTracks(
                      tracks.map((track) => ({
                        ...track,
                        pinned: active.id === getTrackType(track) ? undefined : track.pinned,
                      })),
                    );
                  }
                }}
              >
                <div className={styleContextualMenu.category_title}>Pinned tracks</div>
                <DroppableList id={TOP_TRACKS_LIST_ID}>
                  <SortableContext items={topTracksPinnedList.map(getTrackType)} strategy={verticalListSortingStrategy}>
                    {topTracksPinnedList
                      .filter((track) => onFilter(filter, track))
                      .map((track) => (
                        <DraggableTrackItem key={getTrackType(track)} track={track} onCheckboxClick={onCheckboxClick} />
                      ))}
                  </SortableContext>
                </DroppableList>
                <div className={styleContextualMenu.category_title}>Tracks</div>
                <DroppableList id={MIDDLE_TRACKS_LIST_ID}>
                  <SortableContext
                    items={middleTracksScrollableList.map(getTrackType)}
                    strategy={verticalListSortingStrategy}
                  >
                    {middleTracksScrollableList
                      .filter((track) => onFilter(filter, track))
                      .map((track) => (
                        <DraggableTrackItem key={getTrackType(track)} track={track} onCheckboxClick={onCheckboxClick} />
                      ))}
                  </SortableContext>
                </DroppableList>
                <div className={styleContextualMenu.category_title}>Pinned tracks</div>
                <DroppableList id={BOTTOM_TRACKS_LIST_ID}>
                  <SortableContext
                    items={bottomTracksPinnedList.map(getTrackType)}
                    strategy={verticalListSortingStrategy}
                  >
                    {bottomTracksPinnedList
                      .filter((track) => onFilter(filter, track))
                      .map((track) => (
                        <DraggableTrackItem key={getTrackType(track)} track={track} onCheckboxClick={onCheckboxClick} />
                      ))}
                  </SortableContext>
                </DroppableList>
                <DragOverlay>
                  {draggedTrack && (
                    <TrackItem
                      track={draggedTrack}
                      onCheckboxClick={() => {
                        /*empty */
                      }}
                    />
                  )}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        </Dialog>
      )}
    </ModalControlledOverlay>
  );
};

const DroppableList: FC<PropsWithChildren<UseDroppableArguments>> = (props) => {
  const { setNodeRef } = useDroppable(props);
  return (
    <div className={style.droppable_list} ref={setNodeRef}>
      {props.children}
    </div>
  );
};

interface TrackItemProps {
  track: ISortableTrack;
  onCheckboxClick: any;
}

const DraggableTrackItem: FC<TrackItemProps> = (props) => {
  const trackKey = getTrackType(props.track);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: trackKey,
    data: { track: props.track }, // pass to the DndContext ([active, over].data.current.track)
  });
  const dragStyle = transform
    ? ({ transform: `translate3d( 0, ${transform.y}px, 0)`, transition } as CSSProperties)
    : undefined;

  return (
    <TrackItem
      track={props.track}
      onCheckboxClick={props.onCheckboxClick}
      {...attributes}
      {...listeners}
      dragStyle={{ opacity: isDragging ? 0 : 1, ...dragStyle }}
      ref={setNodeRef}
    />
  );
};

const TrackItem = forwardRef<HTMLDivElement, PropsWithChildren<TrackItemProps> & { dragStyle?: CSSProperties }>(
  function TrackItem({ track, onCheckboxClick, dragStyle, children: _, ...props }, ref) {
    const trackKey = getTrackType(track);
    const statistics = useSelector<IAppState, GenomeBrowserStatistics>(
      (state) => state.genomeBrowser.statistics,
      shallowEqual,
    );

    const trackName: string = conditionallyPluralizeTrackLabel(track);

    const provideTooltip = useCallback(
      (track: ISortableTrack): string | null => {
        let content: string | null = null;

        if (track.type === TRACK_TYPE.VARIANT) {
          if (statistics.hasGenomeAnalysis) {
            content = `Display genomic variants data which has been pre-calculated relative to the current genome.`;
          } else if (!statistics.hasGenomeAnalysis) {
            content = `Genomic variants have not been pre-calculated relative to the current genome.\n
                   Contact your system administrator to have variant data included for this genome.`;
          }
        } else if (track.type === TRACK_TYPE.VCF_VARIANT) {
          if (statistics.hasVcfGenomeAnalysis) {
            content = `Display genomic variants data relative to the current genome, retrieved from a VCF file.`;
          } else if (!statistics.hasVcfGenomeAnalysis) {
            content = `No variant data is available relative to the current genome.\n
                   Contact your system administrator to have variant data included for this genome.`;
          }
        }

        return content;
      },
      [statistics],
    );

    const showTrackType = useMemo(
      () =>
        track.type === TRACK_TYPE.NUFEATURE ||
        track.type === TRACK_TYPE.AAFEATURE ||
        track.type === TRACK_TYPE.MARKER_NUS,
      [track],
    );

    const trackDetails: string = useMemo(() => {
      if (track.type === TRACK_TYPE.BED) {
        const filesCount = track.trackCustomization?.bedConfiguration?.orderedBedFiles?.length;
        return filesCount ? `${filesCount} ${pluralizeWord("file", filesCount)}` : null;
      } else if (track.type === TRACK_TYPE.NGS) {
        const filesCount = track.trackCustomization?.ngsConfiguration?.orderedBamFiles?.length;
        return filesCount ? `${filesCount} ${pluralizeWord("file", filesCount)}` : null;
      }
    }, [track]);

    const tooltip = useMemo(() => provideTooltip(track), [provideTooltip, track]);
    const triggerRef = useDOMRef(ref);
    const tooltipRef = useRef(null);
    const { state, tooltipProps, arrowProps, triggerProps } = useTooltip(triggerRef, tooltipRef, {
      isDisabled: !tooltip,
      placement: "right",
      offset: 10,
      delay: 200,
    });

    return (
      <>
        <div className={style.track_item} style={dragStyle} ref={triggerRef} {...mergeProps(props, triggerProps)}>
          <SymbolIcon icon={"drag_indicator"} />
          <TrackVisibilityCheckbox
            isSelected={!track.hidden}
            isDisabled={noTrackDataAvailable(track, statistics)}
            isDragging={false}
            id={`track_${trackKey}`}
            onChange={(isSelected) => onCheckboxClick(isSelected, track)}
          >
            <div className={style.track}>
              <div>
                {trackName}
                {trackDetails && <span className={style.track_details}>({trackDetails})</span>}
              </div>
              <HStack>
                {showTrackType && <span className={style.track_type}>{FEATURE_DISPLAY_NAME[track.type!]}</span>}
                {noTrackDataAvailable(track, statistics) && <span className={style.track_no_data}>(No data)</span>}
                {tooltip != undefined && <SymbolIcon icon={"info"} size={16} />}
              </HStack>
            </div>
          </TrackVisibilityCheckbox>
        </div>
        {state.isOpen && (
          <Tooltip
            domProps={tooltipProps}
            arrowProps={arrowProps}
            content={tooltip}
            ref={tooltipRef}
            intent={TooltipIntent.Info}
          />
        )}
      </>
    );
  },
);

interface TrackVisibilityCheckboxProps extends AriaCheckboxProps {
  id: string;
  isDragging: boolean;
}

const TrackVisibilityCheckbox = forwardRef<HTMLLabelElement, TrackVisibilityCheckboxProps>((props, forwardedRef) => {
  const state = useToggleState({
    ...props,
    isDisabled: props.isDisabled || props.isDragging,
  });
  const ref = useRef<HTMLInputElement>(null);
  const { inputProps } = useCheckbox(props, state, ref);
  // TODO: const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <label
      className={style.track_eye}
      style={{ cursor: props.isDisabled ? "default" : "pointer" }}
      ref={forwardedRef}
      data-testid={inputProps.id}
      data-visibility={state.isSelected && !props.isDisabled ? "visible" : props.isDisabled ? "disabled" : "hidden"}
    >
      <VisuallyHidden>
        {/* TODO: Add {...focusProps} */}
        <input {...inputProps} ref={ref} />
      </VisuallyHidden>
      <SymbolIcon
        icon={state.isSelected && !props.isDisabled ? "visibility" : props.isDisabled ? "block" : "visibility_off"}
        style={{ color: state.isSelected && !props.isDisabled ? SPECIAL_COLOR.ACTIVE : GREY_COLOR.WHITE_2 }}
      />
      <span
        className={style.track_label}
        style={{ color: props.isDisabled ? GREY_COLOR.COOL_GREY_LIGHT : GREY_COLOR.DARK_GREY }}
      >
        {props.children}
      </span>
    </label>
  );
});

/* --- Utilities --- */
export const noTrackDataAvailable = (track: ISortableTrack, statistics: GenomeBrowserStatistics): boolean => {
  switch (track.type) {
    case TRACK_TYPE.VARIANT:
      return !statistics.hasGenomeAnalysis;
    case TRACK_TYPE.VCF_VARIANT:
      return !statistics.hasVcfGenomeAnalysis;
    case TRACK_TYPE.AAFEATURE:
      // eslint-disable-next-line no-case-declarations
      const aaFeatureStats = statistics?.aaFeaturesCount?.find(
        (stats: AaFeatureStats) => track.subTypeId === stats.featureTypeId,
      );
      return !aaFeatureStats?.count;
    case TRACK_TYPE.NUFEATURE:
      // eslint-disable-next-line no-case-declarations
      const nuFeatureStats = statistics?.nuFeaturesCount?.find(
        (stats: NuFeatureStats) => track.subTypeId === stats.featureTypeId,
      );
      return !nuFeatureStats?.count;
    case TRACK_TYPE.MARKER_NUS:
      // eslint-disable-next-line no-case-declarations
      const markerStats = statistics?.markersCount?.find(
        (stats: MarkerStats) => track.subTypeId === stats.markerTypeId,
      );
      return !markerStats?.count;
    case TRACK_TYPE.PROFILE:
      // eslint-disable-next-line no-case-declarations
      const nuProfileStats = statistics?.nuProfilesCount?.find(
        (stats: NuProfileStats) => track.subTypeId === stats.profileTypeId,
      );
      return !nuProfileStats?.count;
    default:
      return false;
  }
};

export const getTrackType = (track: Track) => `${track.type}-${track.name!.replace(/[^A-Za-z0-9._-]/g, "-")}`;

export default TrackSelectionDialog;
